import React, { useCallback, useState } from 'react';
import { CandidateProfileModal } from '../CandidateProfileModal';
import { AddToInviteListModal } from '../AddToInviteListModal';
import { BankSearchResult } from '../../../../utils/types/candi_bank_types';
import { UseBankSearchReturn, useBankSearch } from '../../hooks/useBankSearch';
import { bankResultToCandidateType } from '../../utils/bankSearchAdapters';
import { CandidateType } from '../../constants/mockData';
import { BankSearchList } from './BankSearchList';
import { NewSearchModal } from './NewSearchModal';
import { BankSearchProcessing } from './BankSearchProcessing';
import { BankSearchEditor } from './BankSearchEditor';
import { CoverageBanner } from './CoverageBanner';
import { BankSearchResultsTable } from './BankSearchResultsTable';
import { UnverifiedDrawer } from './UnverifiedDrawer';

interface BankSearchTabProps {
  state?: UseBankSearchReturn;
}

export const BankSearchTab: React.FC<BankSearchTabProps> = ({ state: externalState }) => {
  const internalState = useBankSearch();
  const state = externalState || internalState;

  // Profile / invite modals (reusing existing components)
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [selectedCandidate, setSelectedCandidate] =
    useState<CandidateType | null>(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteCandidate, setInviteCandidate] =
    useState<CandidateType | null>(null);

  const handleViewProfile = useCallback((result: BankSearchResult) => {
    setSelectedCandidate(bankResultToCandidateType(result));
    setShowProfileModal(true);
  }, []);

  const handleInvite = useCallback((result: BankSearchResult) => {
    setInviteCandidate(bankResultToCandidateType(result));
    setShowInviteModal(true);
  }, []);

  const handleCloseProfile = useCallback(() => {
    setShowProfileModal(false);
    setSelectedCandidate(null);
  }, []);

  // Determine what to show in the right panel
  const showProcessing =
    state.isProcessing ||
    state.pollerState === 'polling' ||
    state.pollerState === 'slow' ||
    state.pollerState === 'timeout' ||
    (state.activeSearch &&
      state.activeSearch.status === 'Error' &&
      !!state.processingError);

  const showEditor =
    state.activeSearch &&
    state.activeSearch.status === 'Completed' &&
    !state.isProcessing;

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open':
        return 'text-emerald-600 bg-emerald-50';
      default:
        return 'text-slate-600 bg-slate-100';
    }
  };

  const getStatusText = (status: string) => status;

  const getMatchColor = (percentage: number) => {
    if (percentage >= 90) return 'bg-emerald-500';
    if (percentage >= 80) return 'bg-cyan-500';
    if (percentage >= 70) return 'bg-blue-500';
    return 'bg-slate-500';
  };

  return (
    <div className='flex flex-col lg:flex-row gap-5 min-h-[600px] w-full'>
      {/* Left panel: saved search list (sleek & space-efficient) */}
      <div className='w-full lg:w-72 flex-shrink-0'>
        <div className='flex items-center justify-between mb-3 px-1'>
          <h2 className='text-xs font-bold text-slate-700 uppercase tracking-wider'>Saved Searches</h2>
          <button
            onClick={() => state.setShowCreateModal(true)}
            className='inline-flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold rounded-lg transition-all shadow-sm active:scale-[0.98]'
          >
            <svg className='w-3.5 h-3.5' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5'>
              <line x1='12' y1='5' x2='12' y2='19' />
              <line x1='5' y1='12' x2='19' y2='12' />
            </svg>
            New Search
          </button>
        </div>
        <BankSearchList
          searches={state.searches}
          total={state.searchListTotal}
          page={state.searchListPage}
          loading={state.searchListLoading}
          activeSearchId={state.activeSearch?.id ?? null}
          onSelect={state.selectSearch}
          onDelete={state.handleDelete}
          onPageChange={state.setSearchListPage}
        />
      </div>

      {/* Right panel: editor + results */}
      <div className='flex-1 min-w-0 flex flex-col gap-4'>
        {!state.activeSearch && !state.isProcessing ? (
          /* Apple-style empty state */
          <div className='flex flex-col items-center justify-center py-20 px-4 text-center bg-white rounded-2xl border border-slate-200/80 shadow-xs'>
            <div className='flex h-14 w-14 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600 mb-3 shadow-inner'>
              <svg className='w-7 h-7' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2'>
                <circle cx='11' cy='11' r='8' />
                <line x1='21' y1='21' x2='16.65' y2='16.65' />
              </svg>
            </div>
            <h3 className='text-base font-bold text-slate-800 mb-1'>
              AI Candidate Matcher
            </h3>
            <p className='text-xs text-slate-500 max-w-sm leading-relaxed mb-5 font-medium'>
              Select a saved search from the sidebar or click &ldquo;New Search&rdquo; to extract requirements from an opening, pasted JD, or file upload.
            </p>
            <button
              onClick={() => state.setShowCreateModal(true)}
              className='inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white text-xs font-bold rounded-xl hover:bg-indigo-700 transition-all shadow-md shadow-indigo-200 active:scale-[0.98]'
            >
              <svg className='w-3.5 h-3.5' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2.5'>
                <line x1='12' y1='5' x2='12' y2='19' />
                <line x1='5' y1='12' x2='19' y2='12' />
              </svg>
              Start New Search
            </button>
          </div>
        ) : showProcessing ? (
          <BankSearchProcessing
            pollerState={
              state.pollerState as
                | 'idle'
                | 'polling'
                | 'slow'
                | 'timeout'
                | 'completed'
                | 'error'
            }
            errorMessage={state.processingError}
            onRetry={state.retrySearch}
            searchTitle={state.activeSearch?.title ?? null}
          />
        ) : showEditor && state.activeSearch ? (
          <div className='flex flex-col gap-3.5'>
            {/* Compact Profile Editor */}
            <BankSearchEditor
              search={state.activeSearch}
              pool={state.pool}
              isReindexing={state.isReindexing}
              onPatchSkills={state.patchSkills}
              onPatchJobTitle={state.patchJobTitle}
              onPatchEducations={state.patchEducations}
              onPatchCertifications={state.patchCertifications}
              onPatchRemoveMatches={state.patchRemoveMatches}
              onPatchExperience={state.patchExperience}
              onPatchTitle={state.patchTitle}
              onRefreshFromOpening={state.handleRefreshFromOpening}
              weightOverride={state.weightOverride}
              onWeightOverrideChange={state.setWeightOverride}
              onCommitWeights={state.commitWeights}
              onResetWeights={state.resetWeights}
              onApplyWeightOverride={state.applyWeightOverride}
            />

            {/* Compact Coverage Banner */}
            <CoverageBanner
              pool={state.pool}
              skillMatchesStale={state.skillMatchesStale}
            />

            {/* Results Table with NameEmailAvatar */}
            <BankSearchResultsTable
              results={state.results}
              pool={state.pool}
              search={state.activeSearch}
              total={state.resultsTotal}
              page={state.resultsPage}
              limit={20}
              loading={state.resultsLoading}
              onPageChange={state.setResultsPage}
              onViewProfile={handleViewProfile}
              onInvite={handleInvite}
            />

            {/* Unverified Drawer */}
            {state.pool && state.pool.unverified > 0 && (
              <UnverifiedDrawer
                unverifiedCount={state.pool.unverified}
                expanded={state.showUnverified}
                onToggle={() =>
                  state.setShowUnverified(!state.showUnverified)
                }
                unverifiedResults={state.unverifiedResults}
                onViewProfile={handleViewProfile}
              />
            )}
          </div>
        ) : null}
      </div>

      {/* New Search Modal */}
      <NewSearchModal
        isOpen={state.showCreateModal}
        onClose={() => state.setShowCreateModal(false)}
        onCreateFromFile={state.createFromFile}
        onCreateFromText={state.createFromText}
        onCreateFromOpening={state.createFromOpening}
        loading={state.createLoading}
      />

      {/* Reuse existing candidate modals */}
      <CandidateProfileModal
        candidate={selectedCandidate}
        isOpen={showProfileModal}
        onClose={handleCloseProfile}
        getStatusColor={getStatusColor}
        getStatusText={getStatusText}
        getMatchColor={getMatchColor}
        onInvite={() => {
          if (selectedCandidate) {
            setInviteCandidate(selectedCandidate);
            setShowInviteModal(true);
          }
        }}
      />

      <AddToInviteListModal
        isOpen={showInviteModal}
        onClose={() => {
          setShowInviteModal(false);
          setInviteCandidate(null);
        }}
        candidateIds={inviteCandidate ? [inviteCandidate.id] : []}
        candidateName={inviteCandidate?.name}
      />
    </div>
  );
};
